import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { productApi, type Product, type ActivityEntry, type AuditEntry } from '../../api/products';

export function ProductDetailPage() {
  const { id = '' } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([productApi.get(id), productApi.activity(id), productApi.audit(id)])
      .then(([p, a, au]) => { setProduct(p); setActivity(a); setAudit(au); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load product'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="p-6">Loading…</p>;
  if (error || !product) return <p className="p-6 text-red-600">{error || 'Not found'}</p>;

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/products" className="text-sm text-slate-600 hover:underline">← Products</Link>
      <h1 className="mt-4 text-2xl font-semibold">{product.name}</h1>
      <p className="text-sm capitalize text-slate-500">{product.status.replace(/_/g, ' ')} · {product.type}</p>
      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        <Link to={`/products/${id}/edit`} className="text-slate-700 hover:underline">Edit</Link>
        <Link to={`/products/${id}/approval`} className="text-slate-700 hover:underline">Approval</Link>
        <Link to={`/products/${id}/health`} className="text-slate-700 hover:underline">Health & quality</Link>
        <Link to={`/products/${id}/relationships`} className="text-slate-700 hover:underline">Relationships</Link>
      </div>
      <div className="mt-6 grid gap-4 rounded-xl border bg-white p-6 text-sm md:grid-cols-2">
        <div><span className="text-slate-500">SKU</span><p>{product.identity?.sku || '—'}</p></div>
        <div><span className="text-slate-500">Brand</span><p>{product.brand || '—'}</p></div>
        <div className="md:col-span-2"><span className="text-slate-500">Description</span><p>{product.description || '—'}</p></div>
      </div>
      <section className="mt-8">
        <h2 className="font-medium">Activity</h2>
        <ul className="mt-3 space-y-2 text-sm">{activity.map((a) => <li key={a.id} className="rounded border px-3 py-2">{a.summary}</li>)}</ul>
      </section>
      <section className="mt-6">
        <h2 className="font-medium">Audit</h2>
        <ul className="mt-3 space-y-2 text-sm">{audit.map((a) => <li key={a.id} className="rounded border px-3 py-2">{a.action} · {a.outcome}</li>)}</ul>
      </section>
    </div>
  );
}
